'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import {
	NavigationCase,
	NavigationCategory,
	NavigationOptionKeyWithoutError,
	NavigationResult,
	NavigationState,
} from 'src/components/Navigation/types';
import { BASE_NAVIGATION_OPTIONS } from 'src/components/Navigation/config/consts';
import { useSwitchMarket } from 'src/hooks/driftActions/market/useSwitchMarket';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useSafePush from 'src/hooks/useSafePush';
import useNavigationStore from 'src/stores/useNavigationStore';
import { openUrl } from 'src/utils/browser';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { MarketType } from '@drift-labs/sdk';
import { ENUM_UTILS } from '@drift/common';
import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import { getIsZeroFeesActiveForMarket } from 'src/utils/zeroFees';
import useModal from 'src/components/Modals/hooks/useModal';

/**
 * Manages navigation state.
 *
 * All state, state changes, router navigation should run through here using the API available.
 */
const useNavigationState = (): NavigationState => {
	const pathname = usePathname() ?? '';
	const searchParams = useSearchParams();
	const navigationStoreState = useNavigationStore();
	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);
	const { show } = useModal();

	const setShowMobileNavMenu = (newValue: boolean) => {
		navigationStoreState.set((s) => {
			s.showMobileNavMenu = newValue;
		});
	};
	const setCurrentNavCategory = (newValue: NavigationCategory | undefined) => {
		navigationStoreState.set((s) => {
			s.currentNavigationCategory = newValue;
		});
	};

	const setCurrentNavSelection = (
		newValue: NavigationOptionKeyWithoutError | undefined
	) => {
		navigationStoreState.set((s) => {
			s.currentNavigationSelection = newValue;
		});
	};

	const { switchMarket } = useSwitchMarket();
	const safePush = useSafePush();

	const isMobile = useIsMobileScreenSize();

	useEffect(() => {
		if (!isMobile) {
			setShowMobileNavMenu(false);
		}
	}, [isMobile]);

	const goToMarketPage = (marketType: MarketType) => {
		if (!ENUM_UTILS.match(selectedMarket?.marketType, marketType)) {
			const newSelectedMarket = {
				marketIndex:
					ENUM_UTILS.match(marketType, MarketType.SPOT) ||
					getIsZeroFeesActiveForMarket({ marketIndex: 1, isPerp: true })
						? 1
						: 0,
				marketType,
			};

			// if switching between spot and perp go to SOL market, page route will automatically be handled when .switchMarket is called
			switchMarket({
				marketIndex: newSelectedMarket.marketIndex,
				marketType: newSelectedMarket.marketType,
				goToTradePage: true,
			});
			return;
		}

		// Should try go to the previously used market
		safePush(`/${selectedMarket?.market.symbol}`);
	};

	const navigateToRoute = (key: NavigationOptionKeyWithoutError) => {
		const navOption = BASE_NAVIGATION_OPTIONS[key];
		handleNavigation(navOption.handler());
	};

	const handleNavigation = (navigationResult: NavigationResult) => {
		setShowMobileNavMenu(false);
		switch (navigationResult.type) {
			case NavigationCase.NAVIGATE_TO_ROUTE: {
				if (navigationResult?.opts?.isMarketNavigation) {
					goToMarketPage(
						navigationResult.opts.marketNavigationType as MarketType
					);
					return;
				}

				if (navigationResult?.opts?.queryOption) {
					safePush(
						`${navigationResult.route}?${navigationResult?.opts?.queryOption}`
					);
					return;
				}

				safePush(navigationResult.route);
				return;
			}
			case NavigationCase.OPEN_MODAL: {
				show(navigationResult.modal);
				return;
			}
			case NavigationCase.OPEN_WINDOW: {
				openUrl(navigationResult.href);
				return;
			}
			case NavigationCase.OPEN_WIDGET: {
				return;
			}
		}
	};

	const navigationState = useMemo(() => {
		return {
			handleNavigation,
			navigateToRoute,
		};
	}, [navigationStoreState]);

	const spotMarkets = useSpotMarkets();

	useEffect(() => {
		//redirect if user goes to old DLP path
		if (pathname.includes('earn/DLP')) {
			safePush('/BAL');
			return;
		}

		const splitPath = pathname.split('/');
		const pathChunks = splitPath.slice(1);
		const pathBase = pathChunks[0];

		const spotBases = spotMarkets.map((market) => market.symbol);

		switch (pathBase) {
			case 'portfolio':
				setCurrentNavCategory('portfolio');
				return;

			// trade
			case 'swap':
				setCurrentNavCategory('trade');
				setCurrentNavSelection('swap');
				return;

			case 'vaults':
				setCurrentNavCategory('vaults');
				if (pathChunks[1] === 'strategy-vaults') {
					setCurrentNavSelection('vaults');
				} else if (pathChunks[1] === 'insurance-fund-vaults') {
					setCurrentNavSelection('insuranceStaking');
				}
				return;

			// bet
			case 'bet':
				setCurrentNavCategory('more');
				setCurrentNavSelection('bet');
				return;

			// earn
			case 'earn':
				setCurrentNavCategory('earn');
				if (pathChunks[1] === 'stake') {
					setCurrentNavSelection('insuranceStaking');
				} else if (pathChunks[1] === 'borrow-lend') {
					setCurrentNavCategory('earn');
					setCurrentNavSelection('borrowLend');
				} else if (pathChunks[1] === 'mm-vaults') {
					setCurrentNavCategory('earn');
					setCurrentNavSelection('marketMakingVaults');
				} else if (pathChunks[1] === 'amplify') {
					setCurrentNavCategory('earn');
					setCurrentNavSelection('amplify');
				} else {
					setCurrentNavSelection('driftEarn');
				}
				return;
			case 'borrow':
				setCurrentNavCategory('earn');
				setCurrentNavSelection('borrowLend');
				return;
			case 'borrow-lend':
				setCurrentNavCategory('earn');
				setCurrentNavSelection('borrowLend');
				return;

			// DRIFT
			case 'drift-safety-module':
				setCurrentNavCategory('more');
				setCurrentNavSelection('driftStaking');
				return;

			// stats
			case 'stats':
				setCurrentNavCategory('more');
				setCurrentNavSelection('stats');
				return;
			case 'leaderboard':
				setCurrentNavCategory('leaderboard');
				return;
			case 'funding':
				setCurrentNavCategory('more');
				setCurrentNavSelection('fundingHistory');
				return;
			// no navigation state required in navbar
			case 'bridge':
				setCurrentNavCategory(undefined);
				setCurrentNavSelection(undefined);
				return;
			case 'dev':
				setCurrentNavCategory(undefined);
				setCurrentNavSelection(undefined);
				return;

			case 'onboarding':
				setCurrentNavCategory(undefined);
				setCurrentNavSelection(undefined);
				return;
			case 'institutional':
				setCurrentNavCategory('more');
				setCurrentNavSelection('institutional');
				return;

			case 'referrals':
				setCurrentNavCategory('more');
				setCurrentNavSelection('referrals');
				return;

			default:
				// Handle SPOT and PERP cases, these don't have paths like the other ones
				// TODO: The assumption here is that the blank path is going to be SOL-PERP .. maybe make this configurable so that if this changes then it automatically flows through here too
				if (!pathBase) {
					setCurrentNavCategory('trade');
					setCurrentNavSelection('perpetuals');
				} else {
					setCurrentNavCategory('trade');

					if (pathname.includes('-PERP')) {
						setCurrentNavSelection('perpetuals');
					} else if (spotBases.includes(pathBase)) {
						setCurrentNavSelection('spot');
					} else if (pathname.includes('-BET')) {
						return;
					} else {
						console.error(`UNHANDLED NAVIGATION CATEGORY, Route : ${pathname}`);
					}
					return;
				}
				return;
		}
	}, [pathname, searchParams, spotMarkets]);

	return navigationState;
};

export default useNavigationState;
